import type { Media } from "@/payload-types";

/**
 * Available pre-generated image sizes from Payload CMS.
 *
 * | Name      | Width  | Height | Notes                    |
 * |-----------|--------|--------|--------------------------|
 * | thumbnail | 300px  | auto   | Tiny previews            |
 * | square    | 500px  | 500px  | Square crops             |
 * | small     | 600px  | auto   | Sidebar cards            |
 * | medium    | 900px  | auto   | 2-col cards, half-width  |
 * | large     | 1400px | auto   | Hero images, full-width  |
 * | xlarge    | 1920px | auto   | Backgrounds, fullscreen  |
 * | og        | 1200px | 630px  | Social sharing (cropped) |
 */
export type MediaSize = "thumbnail" | "square" | "small" | "medium" | "large" | "xlarge" | "og";

/**
 * Get a sized URL from a Payload Media object.
 * Returns the requested pre-generated size, falling back to the original URL.
 */
export function getMediaUrl(media: Media | null | undefined, size?: MediaSize): string | null {
    if (!media) return null;

    if (size && media.sizes?.[size]?.url) {
        return media.sizes[size].url;
    }

    return media.url ?? null;
}

/**
 * Resolve a Payload relationship field (number | Media) to a sized URL.
 * Handles both populated (Media object) and unpopulated (number ID) cases.
 */
export function getMediaFieldUrl(
    field: number | Media | null | undefined,
    size?: MediaSize
): string | null {
    if (!field || typeof field === "number") return null;
    return getMediaUrl(field, size);
}
